import React from "react";
import { NavLink } from "react-router-dom";
import {
  LayoutGrid,
  Users,
  Building2,
  FolderArchive,
  Crown,
  Vault,
  Building,
  Camera,
  Share2,
  Shield,
  X,
} from "lucide-react";
import { useAuth } from "../../contexts/AuthContext";

const NAV = [
  { to: "/", label: "Übersicht", icon: LayoutGrid, testid: "nav-uebersicht", end: true },
  { to: "/top-kandidaten", label: "Top-Kandidaten", icon: Crown, testid: "nav-top-kandidaten" },
  { to: "/investoren", label: "Investoren", icon: Users, testid: "nav-investoren" },
  { to: "/vermietung", label: "Vermietung", icon: Building, testid: "nav-vermietung" },
  { to: "/bilder-videos", label: "Bilder & Videos", icon: Camera, testid: "nav-bilder-videos" },
  { to: "/dokumente", label: "Dokumente", icon: FolderArchive, testid: "nav-dokumente" },
  { to: "/expose-links", label: "Exposé-Links", icon: Share2, testid: "nav-expose-links" },
  { to: "/datenraum", label: "Datenraum", icon: Vault, testid: "nav-datenraum" },
  { to: "/objekt", label: "Objekt", icon: Building2, testid: "nav-objekt" },
];

const ADMIN_NAV = [
  { to: "/admin/users", label: "Benutzer", icon: Shield, testid: "nav-admin-users" },
];

const NavContent = ({ onItemClick }) => {
  const { user } = useAuth();
  const isAdmin = user?.rolle === "admin";
  return (
  <>
    <div className="px-6 pt-6 pb-3 flex items-start justify-between">
      <div>
        <div className="pb-eyebrow">Mandat</div>
        <div className="font-display font-semibold text-navy text-lg leading-snug mt-1 tracking-tight">
          Verkauf
          <br />
          Newton Campus
        </div>
      </div>
    </div>

    <nav className="mt-3 flex-1">
      <ul className="flex flex-col">
        {NAV.map(({ to, label, icon: Icon, testid, end }) => (
          <li key={to}>
            <NavLink
              to={to}
              end={end}
              data-testid={testid}
              onClick={onItemClick}
              className={({ isActive }) =>
                [
                  "flex items-center gap-3 px-6 py-3 text-sm font-sans transition-colors duration-200 border-r-2 min-h-[44px]",
                  isActive
                    ? "text-gold bg-ledger border-r-gold font-medium"
                    : "text-navy/75 border-r-transparent hover:bg-ledger hover:text-navy",
                ].join(" ")
              }
            >
              <Icon size={16} strokeWidth={1.6} />
              <span>{label}</span>
            </NavLink>
          </li>
        ))}

        {isAdmin && (
          <>
            <li className="mt-6 px-6 pb-2">
              <div className="pb-eyebrow text-[9px] text-navy/40">System · Admin</div>
            </li>
            {ADMIN_NAV.map(({ to, label, icon: Icon, testid }) => (
              <li key={to}>
                <NavLink
                  to={to}
                  data-testid={testid}
                  onClick={onItemClick}
                  className={({ isActive }) =>
                    [
                      "flex items-center gap-3 px-6 py-3 text-sm font-sans transition-colors duration-200 border-r-2 min-h-[44px]",
                      isActive
                        ? "text-gold bg-ledger border-r-gold font-medium"
                        : "text-navy/75 border-r-transparent hover:bg-ledger hover:text-navy",
                    ].join(" ")
                  }
                >
                  <Icon size={16} strokeWidth={1.6} />
                  <span>{label}</span>
                </NavLink>
              </li>
            ))}
          </>
        )}
      </ul>
    </nav>

    <div className="px-6 py-4 border-t border-navy/10">
      <div className="pb-eyebrow">Verkäufer</div>
      <div className="text-sm text-navy font-sans mt-1">
        Adldinger Unternehmensgruppe
      </div>
    </div>
  </>
  );
};

export const Sidebar = ({ mobileOpen, onMobileClose }) => {
  return (
    <>
      {/* Desktop: persistent sidebar */}
      <aside
        data-testid="app-sidebar"
        className="hidden md:flex w-60 shrink-0 bg-white border-r border-navy/15 flex-col"
      >
        <NavContent />
      </aside>

      {/* Mobile: slide-in drawer */}
      {mobileOpen && (
        <>
          <div
            data-testid="sidebar-backdrop"
            onClick={onMobileClose}
            className="md:hidden fixed inset-0 bg-navy/40 z-40"
          />
          <aside
            data-testid="app-sidebar-mobile"
            className="md:hidden fixed top-0 left-0 h-full w-[280px] bg-white border-r border-navy/15 z-50 flex flex-col"
            style={{ animation: "slide-in-left 0.2s ease-out" }}
          >
            <div className="flex justify-end p-3 border-b border-navy/10">
              <button
                onClick={onMobileClose}
                data-testid="sidebar-close"
                className="text-navy/65 hover:text-navy p-2 -m-2"
                aria-label="Menü schließen"
              >
                <X size={20} />
              </button>
            </div>
            <NavContent onItemClick={onMobileClose} />
          </aside>
          <style>{`@keyframes slide-in-left { from { transform: translateX(-100%); } to { transform: translateX(0); } }`}</style>
        </>
      )}
    </>
  );
};
